import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Box,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Text,
    Textarea,
    Button,
    Stack,
    Icon
} from "@chakra-ui/react";
import { MdWarningAmber } from "react-icons/md";
import { walletDialogPaperSx, walletTextFieldSx, walletCancelButtonSx } from "./dialogStyles";

/**
 * Creates a confiscation request (4-eyes flow). The balance is NOT touched
 * here: a different super admin must approve the request afterwards.
 * @param {string} reason - The reason for confiscation
 * @param {string} userId - The target user ID
 * @param {Function} setReason - State setter to clear the reason field
 * @param {Function} setLoading - State setter for loading flag
 * @param {Function} onClose - Callback to close the dialog
 * @returns {Promise<void>}
 */
async function submitConfiscate(reason, userId, setReason, setLoading, onClose) {
    if (!reason.trim()) {
        toast.error("Veuillez saisir un motif");
        return;
    }
    setLoading(true);
    try {
        await axios.post(`/admin/wallets/confiscate/${userId}`, { reason: reason.trim() });
        toast.success("Demande de confiscation créée. Un autre super admin doit l'approuver.");
        setReason('');
        onClose();
    } catch (error) {
        toast.error(error?.response?.data || "Erreur lors de la demande de confiscation");
    } finally {
        setLoading(false);
    }
}

/** Chakra style props for the confiscate confirm button. */
const confiscateButtonProps = {
    background: "rgba(244, 67, 54, 0.15)",
    color: "#f44336",
    border: "1px solid rgba(244, 67, 54, 0.4)",
    fontWeight: 600,
    _hover: { background: "rgba(244, 67, 54, 0.25)" },
    _disabled: { opacity: 0.5 }
};

/**
 * Dialog component to request confiscation of an artist wallet balance.
 * Creates a pending request that a second super admin must approve (4-eyes).
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {string} props.userId - Target user ID for the API call
 * @returns {React.ReactElement}
 */
function ConfiscateDialog({ open, onClose, userId }) {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent {...walletDialogPaperSx}>
                <ModalHeader color="#f44336" fontWeight={600}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Icon as={MdWarningAmber} />
                        <Box as="span">Demander la confiscation</Box>
                    </Stack>
                </ModalHeader>
                <ConfiscateDialogBody reason={reason} setReason={setReason} />
                <ConfiscateDialogActions
                    reason={reason}
                    loading={loading}
                    onCancel={onClose}
                    onConfirm={() => submitConfiscate(reason, userId, setReason, setLoading, onClose)}
                />
            </ModalContent>
        </Modal>
    );
}

/**
 * Renders the confiscate dialog content with warning text and reason input.
 * @param {Object} props
 * @param {string} props.reason - Current reason text
 * @param {Function} props.setReason - State setter for the reason field
 * @returns {React.ReactElement}
 */
function ConfiscateDialogBody({ reason, setReason }) {
    return (
        <ModalBody>
            <Text color="rgba(255, 255, 255, 0.7)" mb={2} fontSize="0.9rem">
                Cette action crée une demande de confiscation. Le solde n'est pas touché à ce stade : un autre super admin doit approuver la demande (principe des 4 yeux).
            </Text>
            <Text color="rgba(244, 67, 54, 0.8)" mb={4} fontSize="0.8rem" fontWeight={600}>
                La confiscation est irréversible une fois approuvée. Le motif est tracé dans le journal d'audit.
            </Text>
            <Textarea
                rows={3}
                placeholder="Motif (obligatoire)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                {...walletTextFieldSx}
            />
        </ModalBody>
    );
}

/**
 * Renders the confiscate dialog action buttons (cancel and confirm).
 * @param {Object} props
 * @param {string} props.reason - Current reason text
 * @param {boolean} props.loading - Whether the action is processing
 * @param {Function} props.onCancel - Cancel handler
 * @param {Function} props.onConfirm - Confirm handler
 * @returns {React.ReactElement}
 */
function ConfiscateDialogActions({ reason, loading, onCancel, onConfirm }) {
    return (
        <ModalFooter gap={2}>
            <Button onClick={onCancel} {...walletCancelButtonSx}>
                Annuler
            </Button>
            <Button
                onClick={onConfirm}
                isDisabled={loading || !reason.trim()}
                {...confiscateButtonProps}
            >
                {loading ? "Envoi..." : "Créer la demande"}
            </Button>
        </ModalFooter>
    );
}

export default ConfiscateDialog;
